'use client'

import * as React from 'react'
import { useState, useMemo, useCallback } from 'react'
import { ArrowLeft, ArrowRight, Image as ImageIcon, Loader2, Package } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
	type ColumnDef,
	type PaginationState,
	type RowSelectionState,
	type SortingState,
	getCoreRowModel,
	useReactTable,
	flexRender,
} from '@tanstack/react-table'

import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { CurrencyPriceInput, CurrencyOptionsType } from '@/components/form/currency-price-input'
import { useBulkCreatePromotions } from '@/lib/api/promotions'
import { useProductsList, ProductItem } from '@/lib/api/products'
import { formatMoney } from '@/lib/money'
import { DataTablePagination } from '@/components/data-table/data-table-pagination'
import { useDebouncedCallback } from '@/hooks/use-debounced-callback'

// ============================================================================
// Types
// ============================================================================

interface BulkCreateDiscountModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	storeId: string
}

type Step = 'products' | 'config'
type DiscountType = 'bundle' | 'bogo'

// ============================================================================
// Product Selection Table Columns
// ============================================================================

function getSelectableProductColumns(t: (key: string, opts?: any) => string): ColumnDef<ProductItem>[] {
	return [
		{
			id: 'select',
			header: ({ table }) => (
				<Checkbox
					checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
					onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
					aria-label="Select all"
					className="translate-y-0.5"
				/>
			),
			cell: ({ row }) => (
				<div onClick={(e) => { e.stopPropagation(); row.toggleSelected(!row.getIsSelected()) }}>
					<Checkbox checked={row.getIsSelected()} />
				</div>
			),
			enableSorting: false,
			size: 40,
		},
		{
			id: 'image',
			accessorKey: 'image',
			header: () => null,
			cell: ({ row }) => {
				const product = row.original
				return (
					<div className="bg-muted flex h-8 w-8 items-center justify-center rounded border">
						{product.image ? (
							<img src={product.image} alt={product.name} className="h-full w-full rounded object-cover" />
						) : (
							<ImageIcon className="text-muted-foreground h-3.5 w-3.5" />
						)}
					</div>
				)
			},
			enableSorting: false,
			size: 48,
		},
		{
			id: 'name',
			accessorKey: 'name',
			header: () => <span className="text-xs font-medium">{t('stores:productName')}</span>,
			cell: ({ row }) => {
				const product = row.original
				return (
					<div className="min-w-0">
						<div className="truncate text-sm font-medium">{product.name}</div>
						{product.description && (
							<div className="text-muted-foreground truncate text-xs">{product.description.slice(0, 60)}</div>
						)}
					</div>
				)
			},
		},
		{
			id: 'price',
			accessorKey: 'price',
			header: () => <span className="text-xs font-medium">{t('stores:price')}</span>,
			cell: ({ row }) => {
				const product = row.original
				const currencyOptions = product.price?.currencyOptions ?? {}
				const currency = Object.keys(currencyOptions)[0]
				const amount = currencyOptions[currency]?.amount
				if (!amount) return <span className="text-muted-foreground text-xs">-</span>
				return (
					<div className="text-sm">
						{formatMoney(amount, currency)}
						{product.price.recurring && (
							<Badge variant="secondary" className="ml-1.5 text-[10px]">
								{product.price.interval && product.price.intervalCount
									? `/${product.price.intervalCount} ${product.price.interval}`
									: 'recurring'}
							</Badge>
						)}
					</div>
				)
			},
			enableSorting: false,
			size: 120,
		},
	]
}

// ============================================================================
// Step 1: Product Selection
// ============================================================================

function ProductSelectionStep({
	storeId,
	rowSelection,
	onRowSelectionChange,
	t,
}: {
	storeId: string
	rowSelection: RowSelectionState
	onRowSelectionChange: (selection: RowSelectionState) => void
	t: (key: string, opts?: any) => string
}) {
	const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 })
	const [searchQuery, setSearchQuery] = useState('')
	const [debouncedSearch, setDebouncedSearch] = useState('')

	const debouncedSetSearch = useDebouncedCallback((value: string) => {
		setDebouncedSearch(value)
		setPagination((prev) => ({ ...prev, pageIndex: 0 }))
	}, 300)

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.target.value)
		debouncedSetSearch(e.target.value)
	}

	const { data, isLoading } = useProductsList({
		storeId,
		page: pagination.pageIndex,
		per_page: pagination.pageSize,
		sort: 'name',
		sort_order: 'asc',
		filters: debouncedSearch ? { name: debouncedSearch } : {},
	})

	const products = data?.items ?? []
	const total = data?.pagination?.totalItems ?? 0
	const pageCount = Math.ceil(total / pagination.pageSize)

	const columns = useMemo(() => getSelectableProductColumns(t), [t])

	const table = useReactTable({
		data: products,
		columns,
		pageCount,
		state: { pagination, rowSelection },
		getRowId: (row) => row.id,
		onRowSelectionChange: (updater) => {
			const next = typeof updater === 'function' ? updater(rowSelection) : updater
			onRowSelectionChange(next)
		},
		onPaginationChange: setPagination,
		getCoreRowModel: getCoreRowModel(),
		manualPagination: true,
		enableRowSelection: true,
	})

	const selectedCount = Object.keys(rowSelection).length

	return (
		<div className="space-y-3">
			<div className="flex items-center gap-3">
				<Input
					placeholder={t('stores:searchProducts')}
					value={searchQuery}
					onChange={handleSearchChange}
					className="h-8 text-sm"
				/>
				{selectedCount > 0 && (
					<Badge variant="secondary" className="shrink-0">
						{selectedCount} {t('stores:selected')}
					</Badge>
				)}
			</div>

			<div className="border rounded-lg overflow-hidden">
				<Table>
					<TableHeader>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => (
									<TableHead key={header.id} style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}>
										{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
									</TableHead>
								))}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{isLoading ? (
							Array.from({ length: 5 }).map((_, i) => (
								<TableRow key={i}>
									{columns.map((_, j) => (
										<TableCell key={j}>
											<div className="h-4 w-full animate-pulse rounded bg-muted" />
										</TableCell>
									))}
								</TableRow>
							))
						) : products.length === 0 ? (
							<TableRow>
								<TableCell colSpan={columns.length} className="h-24 text-center">
									<div className="flex flex-col items-center gap-2 text-muted-foreground">
										<Package className="h-8 w-8" />
										<span className="text-sm">{t('stores:noProductsFound')}</span>
									</div>
								</TableCell>
							</TableRow>
						) : (
							table.getRowModel().rows.map((row) => (
								<TableRow
									key={row.id}
									data-state={row.getIsSelected() && 'selected'}
									className="cursor-pointer"
									onClick={() => row.toggleSelected(!row.getIsSelected())}
								>
									{row.getVisibleCells().map((cell) => (
										<TableCell key={cell.id}>
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</TableCell>
									))}
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</div>

			<DataTablePagination table={table} />
		</div>
	)
}

// ============================================================================
// Step 2: Configuration
// ============================================================================

function BundleConfigFields({
	storeId,
	minQuantity,
	setMinQuantity,
	maxQuantity,
	setMaxQuantity,
	adjustmentType,
	setAdjustmentType,
	percentage,
	setPercentage,
	currencyOptions,
	setCurrencyOptions,
	t,
}: {
	storeId: string
	minQuantity: string
	setMinQuantity: (v: string) => void
	maxQuantity: string
	setMaxQuantity: (v: string) => void
	adjustmentType: 'percentage' | 'amount'
	setAdjustmentType: (v: 'percentage' | 'amount') => void
	percentage: string
	setPercentage: (v: string) => void
	currencyOptions: CurrencyOptionsType | null
	setCurrencyOptions: (v: CurrencyOptionsType) => void
	t: (key: string, opts?: any) => string
}) {
	return (
		<>
			<div className="grid grid-cols-2 gap-4">
				<div className="space-y-2">
					<Label>
						{t('stores:minProductQuantity')}
						<span className="ml-1 text-destructive">*</span>
					</Label>
					<Input
						type="number"
						min={1}
						step={1}
						value={minQuantity}
						onChange={(e) => setMinQuantity(e.target.value)}
						placeholder="1"
					/>
				</div>
				<div className="space-y-2">
					<Label>{t('stores:maxProductQuantity')}</Label>
					<Input
						type="number"
						min={1}
						step={1}
						value={maxQuantity}
						onChange={(e) => setMaxQuantity(e.target.value)}
						placeholder={t('stores:noLimit')}
					/>
				</div>
			</div>

			<div className="space-y-2">
				<Label>
					{t('stores:adjustmentType')}
					<span className="ml-1 text-destructive">*</span>
				</Label>
				<Select value={adjustmentType} onValueChange={(v) => setAdjustmentType(v as 'percentage' | 'amount')}>
					<SelectTrigger>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="percentage">{t('stores:percentage')}</SelectItem>
						<SelectItem value="amount">{t('stores:fixedAmount')}</SelectItem>
					</SelectContent>
				</Select>
			</div>

			{adjustmentType === 'percentage' ? (
				<div className="space-y-2">
					<Label>
						{t('stores:discountPercentage')}
						<span className="ml-1 text-destructive">*</span>
					</Label>
					<div className="relative">
						<Input
							type="number"
							min={0.01}
							max={100}
							step={0.01}
							value={percentage}
							onChange={(e) => setPercentage(e.target.value)}
							placeholder="10"
						/>
						<span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
					</div>
				</div>
			) : (
				<div className="space-y-2">
					<Label>
						{t('stores:discountAmount')}
						<span className="ml-1 text-destructive">*</span>
					</Label>
					<CurrencyPriceInput
						storeId={storeId}
						values={currencyOptions}
						onChange={(data) => setCurrencyOptions(data.currencyOptions)}
					/>
				</div>
			)}
		</>
	)
}

function BogoConfigFields({
	buyQuantityThreshold,
	setBuyQuantityThreshold,
	quantityToAdd,
	setQuantityToAdd,
	t,
}: {
	buyQuantityThreshold: string
	setBuyQuantityThreshold: (v: string) => void
	quantityToAdd: string
	setQuantityToAdd: (v: string) => void
	t: (key: string, opts?: any) => string
}) {
	return (
		<>
			<div className="space-y-2">
				<Label>
					{t('stores:buyQuantityThreshold')}
					<span className="ml-1 text-destructive">*</span>
				</Label>
				<Input
					type="number"
					min={1}
					step={1}
					value={buyQuantityThreshold}
					onChange={(e) => setBuyQuantityThreshold(e.target.value)}
					placeholder="1"
				/>
			</div>

			<div className="space-y-2">
				<Label>
					{t('stores:quantityToAdd')}
					<span className="ml-1 text-destructive">*</span>
				</Label>
				<Input
					type="number"
					min={1}
					step={1}
					value={quantityToAdd}
					onChange={(e) => setQuantityToAdd(e.target.value)}
					placeholder="1"
				/>
			</div>
		</>
	)
}

function ConfigStep({
	storeId,
	selectedCount,
	discountType,
	setDiscountType,
	minQuantity,
	setMinQuantity,
	maxQuantity,
	setMaxQuantity,
	adjustmentType,
	setAdjustmentType,
	percentage,
	setPercentage,
	currencyOptions,
	setCurrencyOptions,
	buyQuantityThreshold,
	setBuyQuantityThreshold,
	quantityToAdd,
	setQuantityToAdd,
	t,
}: {
	storeId: string
	selectedCount: number
	discountType: DiscountType
	setDiscountType: (v: DiscountType) => void
	minQuantity: string
	setMinQuantity: (v: string) => void
	maxQuantity: string
	setMaxQuantity: (v: string) => void
	adjustmentType: 'percentage' | 'amount'
	setAdjustmentType: (v: 'percentage' | 'amount') => void
	percentage: string
	setPercentage: (v: string) => void
	currencyOptions: CurrencyOptionsType | null
	setCurrencyOptions: (v: CurrencyOptionsType) => void
	buyQuantityThreshold: string
	setBuyQuantityThreshold: (v: string) => void
	quantityToAdd: string
	setQuantityToAdd: (v: string) => void
	t: (key: string, opts?: any) => string
}) {
	return (
		<div className="space-y-6">
			<div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm">
				<Package className="h-4 w-4 text-muted-foreground" />
				<span>
					{t('stores:productsSelected', { count: selectedCount })}
				</span>
			</div>

			<div className="space-y-2">
				<Label>
					{t('stores:discountType')}
					<span className="ml-1 text-destructive">*</span>
				</Label>
				<Select value={discountType} onValueChange={(v) => setDiscountType(v as DiscountType)}>
					<SelectTrigger>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="bundle">{t('stores:bundleDiscount')}</SelectItem>
						<SelectItem value="bogo">{t('stores:bogoDiscount')}</SelectItem>
					</SelectContent>
				</Select>
			</div>

			{discountType === 'bundle' ? (
				<BundleConfigFields
					storeId={storeId}
					minQuantity={minQuantity}
					setMinQuantity={setMinQuantity}
					maxQuantity={maxQuantity}
					setMaxQuantity={setMaxQuantity}
					adjustmentType={adjustmentType}
					setAdjustmentType={setAdjustmentType}
					percentage={percentage}
					setPercentage={setPercentage}
					currencyOptions={currencyOptions}
					setCurrencyOptions={setCurrencyOptions}
					t={t}
				/>
			) : (
				<BogoConfigFields
					buyQuantityThreshold={buyQuantityThreshold}
					setBuyQuantityThreshold={setBuyQuantityThreshold}
					quantityToAdd={quantityToAdd}
					setQuantityToAdd={setQuantityToAdd}
					t={t}
				/>
			)}
		</div>
	)
}

// ============================================================================
// Main Modal
// ============================================================================

export function BulkCreateDiscountModal({ open, onOpenChange, storeId }: BulkCreateDiscountModalProps) {
	const { t } = useTranslation(['stores', 'common'])
	const bulkCreate = useBulkCreatePromotions()

	const [step, setStep] = useState<Step>('products')
	const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
	const [discountType, setDiscountType] = useState<DiscountType>('bundle')

	// Bundle state
	const [minQuantity, setMinQuantity] = useState('1')
	const [maxQuantity, setMaxQuantity] = useState('')
	const [adjustmentType, setAdjustmentType] = useState<'percentage' | 'amount'>('percentage')
	const [percentage, setPercentage] = useState('')
	const [currencyOptions, setCurrencyOptions] = useState<CurrencyOptionsType | null>(null)

	// BOGO state
	const [buyQuantityThreshold, setBuyQuantityThreshold] = useState('1')
	const [quantityToAdd, setQuantityToAdd] = useState('1')

	const selectedProductIds = Object.keys(rowSelection)

	const resetForm = useCallback(() => {
		setStep('products')
		setRowSelection({})
		setDiscountType('bundle')
		setMinQuantity('1')
		setMaxQuantity('')
		setAdjustmentType('percentage')
		setPercentage('')
		setCurrencyOptions(null)
		setBuyQuantityThreshold('1')
		setQuantityToAdd('1')
	}, [])

	const handleClose = useCallback((nextOpen: boolean) => {
		if (!nextOpen) resetForm()
		onOpenChange(nextOpen)
	}, [onOpenChange, resetForm])

	const isConfigValid = () => {
		if (discountType === 'bundle') {
			const min = parseInt(minQuantity)
			if (!min || min < 1) return false
			if (maxQuantity && parseInt(maxQuantity) < min) return false
			if (adjustmentType === 'percentage') {
				const pct = parseFloat(percentage)
				if (!pct || pct <= 0 || pct > 100) return false
			} else {
				if (!currencyOptions || Object.values(currencyOptions).every(o => o.amount === 0)) return false
			}
			return true
		}

		const buyQty = parseInt(buyQuantityThreshold)
		if (!buyQty || buyQty < 1) return false
		const qty = parseInt(quantityToAdd)
		if (!qty || qty < 1) return false
		return true
	}

	const handleSubmit = () => {
		if (discountType === 'bundle') {
			const min = parseInt(minQuantity)
			const max = maxQuantity ? parseInt(maxQuantity) : null

			const action = adjustmentType === 'percentage'
				? { adjustmentType: 'percentage' as const, adjustmentPercentage: parseFloat(percentage) }
				: { adjustmentType: 'amount' as const, adjustmentAmount: currencyOptions! }

			bulkCreate.mutate(
				{
					discountType: 'bundle',
					storeId,
					productIds: selectedProductIds,
					minProductQuantity: min,
					maxProductQuantity: max,
					action,
				},
				{ onSuccess: () => handleClose(false) },
			)
		} else {
			bulkCreate.mutate(
				{
					discountType: 'bogo',
					storeId,
					productIds: selectedProductIds,
					action: {
						buyQuantityThreshold: parseInt(buyQuantityThreshold),
						quantityToAdd: parseInt(quantityToAdd),
					},
				},
				{ onSuccess: () => handleClose(false) },
			)
		}
	}

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="lg:max-w-5xl max-h-[85vh] flex flex-col">
				<DialogHeader>
					<div className="flex items-center gap-3">
						<DialogTitle className="flex-1">
							{step === 'products'
								? t('stores:selectProducts')
								: t('stores:configureDiscount')}
						</DialogTitle>
						<div className="flex items-center gap-1.5 text-xs text-muted-foreground">
							<span className={step === 'products' ? 'font-semibold text-foreground' : ''}>1</span>
							<ArrowRight className="h-3 w-3" />
							<span className={step === 'config' ? 'font-semibold text-foreground' : ''}>2</span>
						</div>
					</div>
					<DialogDescription>
						{step === 'products'
							? t('stores:selectProductsDescription')
							: t('stores:configureDiscountDescription')}
					</DialogDescription>
				</DialogHeader>

				<div className="flex-1 overflow-y-auto py-2">
					{step === 'products' ? (
						<ProductSelectionStep
							storeId={storeId}
							rowSelection={rowSelection}
							onRowSelectionChange={setRowSelection}
							t={t}
						/>
					) : (
						<ConfigStep
							storeId={storeId}
							selectedCount={selectedProductIds.length}
							discountType={discountType}
							setDiscountType={setDiscountType}
							minQuantity={minQuantity}
							setMinQuantity={setMinQuantity}
							maxQuantity={maxQuantity}
							setMaxQuantity={setMaxQuantity}
							adjustmentType={adjustmentType}
							setAdjustmentType={setAdjustmentType}
							percentage={percentage}
							setPercentage={setPercentage}
							currencyOptions={currencyOptions}
							setCurrencyOptions={setCurrencyOptions}
							buyQuantityThreshold={buyQuantityThreshold}
							setBuyQuantityThreshold={setBuyQuantityThreshold}
							quantityToAdd={quantityToAdd}
							setQuantityToAdd={setQuantityToAdd}
							t={t}
						/>
					)}
				</div>

				<DialogFooter className="gap-2 sm:gap-0">
					{step === 'config' && (
						<Button type="button" variant="ghost" onClick={() => setStep('products')} className="mr-auto">
							<ArrowLeft className="mr-1.5 h-4 w-4" />
							{t('common:back')}
						</Button>
					)}
					<Button type="button" variant="outline" onClick={() => handleClose(false)}>
						{t('common:cancel')}
					</Button>
					{step === 'products' ? (
						<Button
							type="button"
							disabled={selectedProductIds.length === 0}
							onClick={() => setStep('config')}
						>
							{t('common:next')}
							<ArrowRight className="ml-1.5 h-4 w-4" />
						</Button>
					) : (
						<Button
							type="button"
							disabled={!isConfigValid() || bulkCreate.isPending}
							onClick={handleSubmit}
						>
							{bulkCreate.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
							{t('stores:createDiscounts', { count: selectedProductIds.length })}
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
